import { useContext, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { updateClassifierResolver } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/EditClassifierDrawer/validationSchema';
import { UpdateDealClassifierDto } from '@dealstack/shared/lib/generated';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { Classifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { buildUpdateData } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils';
import { useUpdateClassifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/hooks/useUpdateClassifier';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

export const useEditClassifierDrawer = (classifier: Classifier) => {
	const { dealId } = useContext(DealContext);
	const tAdminDashboard = useTranslationAdminDashboard();

	const { updateClassifier, isLoading } = useUpdateClassifier(dealId || '');

	const formState = useForm<UpdateDealClassifierDto>({
		resolver: updateClassifierResolver,
		defaultValues: {
			name: classifier.name,
			classifierParentId: classifier.classifierParentId,
			archive: classifier.archive,
		},
	});

	const onSave = useCallback(async () => {
		if (!dealId) {
			showToastV2({
				title: tAdminDashboard('classifier.errors.no-deal-id'),
				state: 'error',
			});
			return;
		}
		if (!classifier.dealClassifierId) {
			showToastV2({
				title: tAdminDashboard('classifier.errors.no-deal-id'),
				state: 'error',
			});
			return;
		}

		const formValues = formState.getValues();
		const updatedData = buildUpdateData({ formValues, dealId, classifier });

		void updateClassifier(updatedData);
	}, [dealId, classifier, formState, updateClassifier, tAdminDashboard]);

	return {
		formState,
		isLoading,
		onSave,
	};
};
